import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'lodash';

import workspacesReducer from '@app/reducers/entities/workspaces';
import agreementsReducer from '@app/reducers/entities/agreements';
import { getAccountFromSessionSelector } from '@app/reducers/session';

import CircularSpinner from '@app/components/icons/circular-spinner';
/* eslint-disable import/named */
import { ApiError, getErrorMessage, unknownApiError } from '@app/components/api-error';
import { ChartCard } from '@app/components/pages/ai-insights/charts/chart-card';
import { checkAcl } from '@flow/libs/utils/acl';

import { useFilterContext } from '@app/hocs/search/filter/context';
import { useAiInsightsContext } from '@app/contexts/ai-insights-context';

import { CONTRACT_NOT_ANALYSED } from '../charts/constants';
import { InsightsEmptyState } from '../insights-empty-state';
import { DisabledState } from './disabled-state';
import { PieInsightChart, StackedBarChart, VerticalBarChart } from '../charts';
import type { InsightsData } from '../types';
import { chartTypes } from '../constants';
import {
  canSeeInsightContent,
  canSeeInsightTab,
  isExtensionEnabled,
  hasActivePlaybook,
} from './utils';

import style from './content.module.scss';
import { NumberChart } from '../charts/number-chart';

const renderActionMessage = (
  error: Error,
  workspace: Oneflow.Workspace,
  account: Oneflow.Account,
) => {
  if (canSeeInsightTab(workspace) && !canSeeInsightContent(workspace)) {
    if (!isExtensionEnabled(account)) {
      return (
        <DisabledState
          extensionDisabled
          hasMarketplaceAccess={checkAcl(account.acl, 'account:admin:extension')}
        />
      );
    }

    if (isExtensionEnabled(account) && !hasActivePlaybook(workspace)) {
      return (
        <DisabledState
          playbookDisabled
          hasPlaybookPermission={checkAcl(workspace.acl, 'collection:ai_review:admin')}
        />
      );
    }
  }

  if (error) {
    const errorCode = get(error, 'body.api_error_code');

    return <ApiError customMessage={getErrorMessage(errorCode) || unknownApiError} />;
  }

  return null;
};

const AI_INSIGHTS_QUERY_AGREEMENTS = 'aiInsights';

export const Content = () => {
  const dispatch = useDispatch();
  const { insightsData, workspaceId } = useAiInsightsContext();
  const { activeFilters } = useFilterContext();
  const { error, loading, success } = useSelector((state) =>
    workspacesReducer.getGetAiInsightsSelector(state, {
      id: workspaceId,
    }),
  );
  const { count: documentCount, loading: documentsLoading } = useSelector((state) =>
    agreementsReducer.getQuerySelector(state, {
      name: AI_INSIGHTS_QUERY_AGREEMENTS,
    }),
  );
  const workspace = useSelector((state) =>
    workspacesReducer.getWorkspaceSelector(state, {
      id: workspaceId,
    }),
  );
  const account = useSelector(getAccountFromSessionSelector);
  const { filteredDocumentCount, filteredDocumentWithIssuesCount, insights } = insightsData || {};

  useEffect(() => {
    if (workspaceId) {
      dispatch(
        workspacesReducer.fetchWorkspace({
          id: workspaceId,
        }),
      );
    }
  }, [
    dispatch,
    workspaceId,
  ]);

  const getChartData = useCallback(
    (data: InsightsData) => {
      const hasUnprocessedDocuments = documentCount - filteredDocumentCount > 0;

      if (hasUnprocessedDocuments) {
        return {
          ...data,
          data: [
            ...data.data,
            {
              label: CONTRACT_NOT_ANALYSED,
              count: documentCount - filteredDocumentCount,
              isBreach: null,
            },
          ],
        };
      }
      return data;
    },
    [
      documentCount,
      filteredDocumentCount,
    ],
  );

  if (loading) {
    return (
      <div className={style.LoaderContainer}>
        <CircularSpinner />
      </div>
    );
  }

  if (renderActionMessage(error, workspace, account)) {
    return renderActionMessage(error, workspace, account);
  }

  if (!insights?.length) {
    return (
      <InsightsEmptyState
        success={success}
        documentsLoading={documentsLoading}
        documentCount={documentCount}
        activeFilters={activeFilters}
        insightsLength={insights?.length}
      />
    );
  }

  return (
    <div className={style.InsightsContainer}>
      <div className={style.NumberChartContainer}>
        <NumberChart
          insightType="all"
          number={filteredDocumentWithIssuesCount}
          hasIssues={true}
        />
        <NumberChart
          insightType="allPassed"
          number={filteredDocumentCount - filteredDocumentWithIssuesCount}
          hasIssues={false}
        />
      </div>
      {insights?.map((data: InsightsData) => {
        const { type, reviewText } = data;
        if (!chartTypes[type] || chartTypes[type].type === 'number') {
          return null;
        }
        return (
          <ChartCard
            key={type}
            insightType={type}
            playbookValues={reviewText?.values}
            hasIssues={data.data.some((d) => d.isBreach)}
          >
            {chartTypes[type].type === 'pie' && (
              <PieInsightChart
                insightType={type}
                data={getChartData(data)}
              />
            )}
            {chartTypes[type].type === 'bar' && (
              <StackedBarChart
                insightType={type}
                data={getChartData(data)}
              />
            )}

            {chartTypes[type].type === 'vertical' && (
              <VerticalBarChart
                insightType={type}
                data={getChartData(data)}
              />
            )}
          </ChartCard>
        );
      })}
    </div>
  );
};
